import { createSlice, createSelector, PayloadAction, nanoid } from '@reduxjs/toolkit';
import type {
  Notification,
  NotificationInput,
  NotificationPriority,
  NotificationSettings,
  NotificationState,
  NotificationType,
} from '../types/notification.types';
import type { RootState } from '../store';

/**
 * Priority order mapping for sorting notifications.
 * Lower values indicate higher priority.
 */
const priorityOrder: Record<NotificationPriority, number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

/**
 * Maximum number of notifications to keep in the queue.
 * Prevents memory leaks from unbounded notification accumulation.
 */
const MAX_QUEUE_SIZE = 50;

/**
 * Initial state for the notification slice.
 */
const initialState: NotificationState = {
  notifications: [],
  settings: {
    maxVisible: 3,
    defaultDuration: 5000,
    soundEnabled: false,
    position: 'top-right',
  },
};

/**
 * Notification slice for managing UI notifications with priority-based queue management.
 */
const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    /**
     * Add a new notification to the queue.
     * Auto-generates id and timestamp, applies defaults, sorts by priority, and enforces queue limit.
     */
    addNotification: {
      reducer(state, action: PayloadAction<Notification>) {
        state.notifications.push(action.payload);

        // Sort by priority (primary) and timestamp (secondary - FIFO for same priority)
        state.notifications.sort((a, b) => {
          const priorityA = priorityOrder[a.priority || 'normal'];
          const priorityB = priorityOrder[b.priority || 'normal'];

          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }

          return a.timestamp - b.timestamp;
        });

        // Enforce queue size limit (keep highest priority notifications)
        if (state.notifications.length > MAX_QUEUE_SIZE) {
          state.notifications = state.notifications.slice(0, MAX_QUEUE_SIZE);
        }
      },
      prepare(input: NotificationInput) {
        return {
          payload: {
            ...input,
            id: nanoid(),
            timestamp: Date.now(),
            priority: input.priority ?? 'normal',
            dismissible: input.dismissible ?? true,
            duration: input.duration ?? 5000,
          } as Notification,
        };
      },
    },

    /**
     * Remove a notification by its ID.
     */
    removeNotification(state, action: PayloadAction<string>) {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload,
      );
    },

    /**
     * Clear all notifications or filter by type.
     */
    clearNotifications(state, action: PayloadAction<NotificationType | undefined>) {
      if (action.payload) {
        state.notifications = state.notifications.filter(
          (notification) => notification.type !== action.payload,
        );
      } else {
        state.notifications = [];
      }
    },

    /**
     * Update notification display settings.
     */
    updateNotificationSettings(state, action: PayloadAction<Partial<NotificationSettings>>) {
      state.settings = {
        ...state.settings,
        ...action.payload,
      };
    },
  },
});

// Export actions
export const {
  addNotification,
  removeNotification,
  clearNotifications,
  updateNotificationSettings,
} = notificationSlice.actions;

// Selectors

/**
 * Select all notifications (sorted by priority).
 */
export const selectNotifications = (state: RootState): Notification[] =>
  state.notification.notifications;

/**
 * Select notification display settings.
 */
export const selectNotificationSettings = (state: RootState): NotificationSettings =>
  state.notification.settings;

/**
 * Select only the visible notifications based on maxVisible setting.
 * Memoized to prevent unnecessary re-renders when notifications/settings haven't changed.
 */
export const selectVisibleNotifications = createSelector(
  [selectNotifications, selectNotificationSettings],
  (notifications, settings): Notification[] => notifications.slice(0, settings.maxVisible),
);

// Export reducer
export default notificationSlice.reducer;
